/**
 * Emergent OAuth Callback Handler
 * 
 * Handles the callback from Emergent managed OAuth.
 * Exchanges session_id for session_token via /api/auth/session.
 * Uses unified callback handler for consistent behavior.
 */
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader2, AlertCircle } from 'lucide-react';
import { API_BASE_URL } from '../config/api';
import { useAuth } from '../contexts/AuthContext';
import {
  processOAuthCallback,
  executeCallbackNavigation,
  parse402Response,
} from '../utils/oauthCallbackHandler';

const BACKEND_URL = API_BASE_URL;

const AuthCallback = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { loginFromOAuth } = useAuth();
  const hasProcessed = useRef(false);
  const [error, setError] = useState(null);
  const [debugInfo, setDebugInfo] = useState('');

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      try {
        // Extract session_id from URL - check both query params and hash fragment
        const hash = location.hash;
        const search = location.search;
        const fullUrl = window.location.href;
        
        console.log('[AuthCallback] Starting auth process');
        console.log('[AuthCallback] Full URL:', fullUrl);
        console.log('[AuthCallback] BACKEND_URL:', BACKEND_URL);
        
        setDebugInfo(`URL: ${fullUrl}\nBackend: ${BACKEND_URL}`);
        
        // Try to get session_id from query params first, then from hash
        let sessionId = null;
        
        // Check query params (e.g., ?session_id=xxx)
        const queryParams = new URLSearchParams(search);
        sessionId = queryParams.get('session_id');
        
        // If not in query params, check hash fragment (e.g., #session_id=xxx)
        if (!sessionId && hash) {
          const hashParams = new URLSearchParams(hash.substring(1));
          sessionId = hashParams.get('session_id');
        }

        console.log('[AuthCallback] Session ID:', sessionId ? 'Found' : 'NOT FOUND');

        if (!sessionId) {
          console.error('[AuthCallback] No session_id found in URL');
          setError(`No session_id in URL. Search: "${search}", Hash: "${hash}"`);
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Get stored pricing info from signup flow
        const signupPricingStr = sessionStorage.getItem('signup_pricing');
        const signupPricing = signupPricingStr ? JSON.parse(signupPricingStr) : null;
        
        // Store session_id BEFORE the API call - signup page can use this if 402 response parsing fails
        sessionStorage.setItem('pending_session_id', sessionId);
        
        // Exchange session_id for session_token
        let response;
        try {
          response = await fetch(`${BACKEND_URL}/api/auth/session`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            credentials: 'include',
            body: JSON.stringify({ 
              session_id: sessionId,
              signup_pricing: signupPricing
            }),
          });
        } catch (fetchError) {
          console.error('[AuthCallback] Fetch failed:', fetchError);
          setError(`Network error: ${fetchError.message}`);
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        console.log('[AuthCallback] Response status:', response.status);

        // Handle 402 Payment Required - new user needs to select a plan
        if (response.status === 402) {
          console.log('[AuthCallback] Got 402 - redirecting to signup pricing');
          sessionStorage.removeItem('signup_pricing');
          // Redirect immediately - signup page will handle getting the token
          window.location.href = `${window.location.origin}/signup/pricing`;
          return;
        }
        
        // Clear pending session_id on success
        sessionStorage.removeItem('pending_session_id');

        // Handle other error responses
        if (!response.ok) {
          let errorText = '';
          try {
            const errorData = await response.json();
            errorText = errorData.detail || JSON.stringify(errorData);
          } catch {
            errorText = `Status ${response.status}`;
          }
          console.error('[AuthCallback] Session creation failed:', response.status, errorText);
          setError(`API Error ${response.status}: ${errorText}`);
          setTimeout(() => navigate('/login'), 5000);
          return;
        }

        // Parse successful response
        let data;
        try {
          data = await response.json();
        } catch (parseError) {
          console.error('[AuthCallback] Failed to parse response:', parseError);
          setError(`Failed to parse server response: ${parseError.message}`);
          setTimeout(() => navigate('/login'), 5000);
          return;
        }
        
        // Clear signup storage now that we have a successful response
        sessionStorage.removeItem('signup_pricing');
        
        console.log('[AuthCallback] Success response:', {
          hasUser: !!data.user,
          hasOrg: !!data.organization,
          isNewUser: data.is_new_user,
          existingUserFromSignup: data.existing_user_from_signup,
          hasCheckoutUrl: !!data.checkout_url,
          requiresCheckout: data.requires_checkout,
        });

        // Handle new user OAuth signup - redirect to Stripe checkout
        // Account will be created AFTER payment succeeds
        if (data.requires_checkout && data.checkout_url) {
          console.log('[AuthCallback] New user OAuth signup - redirecting to Stripe checkout');
          window.location.href = data.checkout_url;
          return;
        }

        // Set auth state BEFORE navigation (Emergent OAuth provides full user/org)
        if (data.user && data.organization) {
          console.log('[AuthCallback] Setting auth state via loginFromOAuth');
          await loginFromOAuth(data.user, data.organization);
        }

        // Use unified callback handler for navigation
        const result = processOAuthCallback({
          sessionToken: data.session_token,
          isNewUser: data.is_new_user,
          userEmail: data.user?.email,
          userRole: data.user?.role,
          onboardingCompleted: data.organization?.onboarding_completed,
          existingUserFromSignup: data.existing_user_from_signup,
          checkoutUrl: data.checkout_url,
          user: data.user,
          organization: data.organization,
        });

        console.log('[AuthCallback] Navigation result:', result);

        // Execute navigation (handles pending invite acceptance if needed)
        await executeCallbackNavigation(result, navigate, BACKEND_URL);
        
      } catch (error) {
        console.error('[AuthCallback] Exception:', error);
        setError(`Error: ${error.message}`);
        setTimeout(() => navigate('/login'), 5000);
      }
    };

    processSession();
  }, [location, navigate, loginFromOAuth]);

  // Show error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 via-white to-orange-50 flex items-center justify-center p-4">
        <div className="text-center max-w-lg">
          <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Error
          </h2>
          <p className="text-gray-600 mb-4">
            {error}
          </p>
          <p className="text-sm text-gray-500 mb-4">
            Redirecting to login in 5 seconds...
          </p>
          {debugInfo && (
            <pre className="text-xs text-left bg-gray-100 p-3 rounded overflow-auto max-h-32">
              {debugInfo}
            </pre>
          )}
          <button
            onClick={() => navigate('/login')}
            className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
            data-testid="auth-error-login-button"
          >
            Go to Login Now
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
          Signing you in...
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Please wait while we complete your authentication
        </p>
      </div>
    </div>
  );
};

export default AuthCallback;
